import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { getSurvey, createSession } from '../services/api';

const CreateSession = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  
  const [survey, setSurvey] = useState(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setLoading(true);
        const data = await getSurvey(surveyId);
        setSurvey(data);
      } catch (err) {
        console.error('Error fetching survey:', err);
        setError('Failed to load survey. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [surveyId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      const sessionData = {
        survey_id: surveyId,
        name: name.trim() || null
      };
      
      console.log('Creating session with data:', sessionData);
      const response = await createSession(sessionData);
      console.log('Session created successfully:', response);
      
      // Check if the response has an ID before navigating
      if (!response || !response.id) {
        throw new Error('Session created but no ID was returned');
      }
      
      // Log the URL we're about to navigate to
      const nextUrl = `/sessions/${response.id}`;
      console.log('Navigating to:', nextUrl);
      
      navigate(nextUrl);
    } catch (err) {
      console.error('Error creating session:', err);
      
      // Get more detailed error information
      let errorMessage = 'Failed to create session. Please try again.';
      
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Server error response:', err.response.data);
        console.error('Status code:', err.response.status);
        errorMessage = `Server error: ${err.response.status}. ${err.response.data?.error || 'Unknown error'}`;
      } else if (err.request) {
        // The request was made but no response was received
        console.error('No response received from server');
        errorMessage = 'No response from server. Please check if the backend is running on the correct port.';
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', err.message);
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
      </Alert>
    );
  }

  if (!survey) {
    return (
      <Alert variant="warning">
        Survey not found. It may have been deleted or the ID is incorrect.
      </Alert>
    );
  }

  return (
    <div>
      <h1 className="mb-4">Start New Session</h1>
      
      <Card className="mb-4">
        <Card.Header>
          <h2 className="h5 mb-0">Survey: {survey.title}</h2>
        </Card.Header>
        <Card.Body>
          <p>{survey.description || 'No description provided.'}</p>
          <div>
            <small className="text-muted">
              {survey.groups?.length || 0} groups • {
                (survey.groups?.reduce((total, group) => total + group.questions.length, 0) || 0) + 
                (survey.general_questions?.length || 0)
              } questions
            </small>
          </div>
        </Card.Body>
      </Card>
      
      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>
            <h2 className="h5 mb-0">Session Details</h2>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Session Name (optional)</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name for this session"
              />
              <Form.Text className="text-muted">
                A unique identifier will be automatically generated if no name is provided.
              </Form.Text>
            </Form.Group>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header>
            <h2 className="h5 mb-0">Session Options</h2>
          </Card.Header>
          <Card.Body>
            <p>After creating the session, you will be able to:</p>
            <ul>
              <li>Fill out the survey manually</li>
              <li>Upload audio files for AI-powered answer extraction</li>
              <li>Use real-time voice input</li>
            </ul>
          </Card.Body>
        </Card>
        
        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate(`/surveys/${surveyId}`)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? 'Creating Session...' : 'Create Session'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreateSession;